/* eslint-disable react/no-array-index-key */
import { useMemo } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import Properties, { renderPageLink, renderSiteList } from '../../properties/Properties';
import usePanelDataService from '../../../services/PanelDataService';
import { stringProperty, listProperty, linkProperty, listLinkProperty } from '../../properties/funcs';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';
import { accessPointFilterId, radioFilterId, timeRangeFilterId, wirelessControllerFilterId } from '../../filters';
import { DEFAULT_INVALID_VALUE, logFormatter } from '../../../utilities/FormattingUtilities';

const dataSource = {
    url: '/query/table',
    params: {
        type: 'radio',
        renderAsEntity: true,
        entityFields: ['deviceAp', 'deviceWlc', 'location', 'band', 'sites'],
        metrics: ['channels', 'txPower', 'txPacketCount', 'rxPacketCount', 'clientConnectionCount'],
    },
};

function usePropertiesColumns(data) {
    const apId = _.get(data, 'radio.deviceAp.uuid');
    const wlcId = _.get(data, 'radio.deviceWlc.uuid');
    return useMemo(() => {
        const apFilters = [{ name: accessPointFilterId, uuid: apId }];
        const wlcFilters = [{ name: wirelessControllerFilterId, uuid: wlcId }];
        return [
            {
                properties: [
                    linkProperty(
                        t`Access Point`,
                        'radio.deviceAp.name',
                        '/access-point-detail',
                        apFilters,
                        [radioFilterId],
                        renderPageLink
                    ),
                    linkProperty(
                        t`Wireless Controller`,
                        'radio.deviceWlc.name',
                        '/wireless-controller-detail',
                        wlcFilters,
                        [radioFilterId],
                        renderPageLink
                    ),
                    stringProperty(t`Location`, 'radio.location'),
                    stringProperty(t`Clients`, 'clientConnectionCount', renderLogFormatter),
                    listLinkProperty(t`Sites`, 'radio.sites', [], [radioFilterId], renderSiteList),
                ],
            },
            {
                properties: [
                    stringProperty(t`Packets In`, 'rxPacketCount', renderLogFormatter),
                    stringProperty(t`Packets Out`, 'txPacketCount', renderLogFormatter),
                    stringProperty(t`TxPower`, 'txPower', renderTxPower),
                    stringProperty(t`Band`, 'radio.band', renderBand),
                    listProperty(t`Channels`, 'channels', renderChannelList),
                ],
            },
        ];
    }, [apId, wlcId]);
}

function renderBand(value) {
    return `${value === 1 ? t`2.4 GHz` : t`5 GHz`}`;
}

function renderChannelList(value) {
    if (!value?.length) {
        return DEFAULT_INVALID_VALUE;
    }

    return (
        <>
            {value.map((channel) => (
                <div key={channel.channelNumber}>{`${channel.channelNumber} (${channel.channelWidth}) MHz`}</div>
            ))}
        </>
    );
}

function renderLogFormatter(value) {
    return logFormatter(value, { maxDecimals: 0 });
}

function renderTxPower(value) {
    return `${value} dBm`;
}

export function RadioProperties({ data }) {
    const propertiesColumns = usePropertiesColumns(data);
    const classes = useStyles();
    return (
        <div className={classes.propertiesPanel}>
            {propertiesColumns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function RadioPropertiesPanel() {
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <RadioProperties data={chartData} />;
}

RadioPropertiesPanel.propTypes = panelPropTypes;

RadioPropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Radio Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'RadioAttributes',
    filters: {
        required: [timeRangeFilterId, radioFilterId],
        optional: [],
    },
})(RadioPropertiesPanel);
